import { norm } from './search';
import type { Unit } from './schema';
import { isTeams, isUrl, isYouTube } from './urls';
import { pad } from './time';

export interface Issue {
  lvl: 'err' | 'warn';
  /** Field the issue belongs to, for linking from the readiness screen. */
  k: 'name' | 'teams' | 'desc' | 'yt' | 'pdf' | 'dup' | 'draft';
  t: string;
}

type UnitLike = Pick<Unit, 'name' | 'teams' | 'description' | 'youtube' | 'pdfs'>;

/** Readiness problems for a single unit. Errors block a good experience; warnings do not. */
export function unitIssues(u: UnitLike): Issue[] {
  const out: Issue[] = [];
  if (!(u.name || '').trim()) out.push({ lvl: 'err', k: 'name', t: 'חסר שם' });
  if (!u.teams) out.push({ lvl: 'err', k: 'teams', t: 'חסר קישור Teams' });
  else if (!isTeams(u.teams)) out.push({ lvl: 'err', k: 'teams', t: 'קישור Teams לא תקין' });
  if (!(u.description || '').trim()) out.push({ lvl: 'warn', k: 'desc', t: 'חסר תיאור' });
  if (u.youtube && !isYouTube(u.youtube))
    out.push({ lvl: 'err', k: 'yt', t: 'קישור YouTube לא תקין' });
  (u.pdfs || []).forEach((p, i) => {
    if (!isUrl(p.url)) out.push({ lvl: 'err', k: 'pdf', t: `קישור PDF ${i + 1} לא תקין` });
  });
  return out;
}

export const isReady = (u: UnitLike): boolean => unitIssues(u).length === 0;

/** Zero-padded next free booth number. */
export function nextBooth(units: Pick<Unit, 'booth'>[]): string {
  return pad(Math.max(0, ...units.map((u) => parseInt(u.booth, 10) || 0)) + 1);
}

export interface UnitFormFields {
  name: string;
  booth: string;
  teams: string;
  youtube: string;
  pdfs: { title: string; url: string }[];
}

/** Admin editor validation. Returns a map of field -> Hebrew error; empty when valid. */
export function validateUnitForm(
  f: UnitFormFields,
  units: Pick<Unit, 'id' | 'name' | 'booth'>[],
  selfId?: string,
): Record<string, string> {
  const e: Record<string, string> = {};
  if (!f.name.trim()) e.name = 'שם היחידה הוא שדה חובה';
  else if (units.some((u) => u.id !== selfId && norm(u.name) === norm(f.name)))
    e.name = 'כבר קיימת יחידה בשם הזה';

  const booth = f.booth.trim();
  if (booth && !/^\d{1,3}$/.test(booth)) e.booth = 'יש להזין מספר בין 1 ל-999';
  else if (booth) {
    const other = units.find(
      (u) => u.id !== selfId && parseInt(u.booth, 10) === parseInt(booth, 10),
    );
    if (other) e.booth = `מספר הדוכן כבר משויך ל${other.name}`;
  }

  if (f.teams.trim() && !isTeams(f.teams.trim()))
    e.teams = 'הקישור צריך להתחיל ב-https://teams.microsoft.com/';
  if (f.youtube.trim() && !isYouTube(f.youtube.trim()))
    e.youtube = 'יש להזין קישור מ-youtube.com או youtu.be';
  f.pdfs.forEach((p, i) => {
    if (!p.url.trim()) e['pdf' + i] = 'חסר קישור לקובץ';
    else if (!isUrl(p.url.trim())) e['pdf' + i] = 'הקישור צריך להתחיל ב-https://';
  });
  return e;
}

/** Warnings across the whole list, e.g. duplicate names and open drafts. */
export function listIssues(units: Pick<Unit, 'id' | 'name' | 'status'>[]): Map<string, Issue[]> {
  const out = new Map<string, Issue[]>();
  const add = (id: string, i: Issue) => out.set(id, [...(out.get(id) ?? []), i]);
  const seen = new Map<string, string>();
  for (const u of units) {
    const key = norm(u.name);
    if (key && seen.has(key)) {
      add(u.id, { lvl: 'warn', k: 'dup', t: 'שם כפול' });
      add(seen.get(key)!, { lvl: 'warn', k: 'dup', t: 'שם כפול' });
    } else if (key) seen.set(key, u.id);
    if (u.status === 'draft') add(u.id, { lvl: 'warn', k: 'draft', t: 'טיוטה שלא פורסמה' });
  }
  return out;
}

/* ---- domain look: tone and icon are derived from keywords in the domain name ---- */

const DOM_ICON: [RegExp, string][] = [
  [/פליל/, 'gavel'],
  [/אזרח/, 'scale'],
  [/ציבורי|חוקתי|חקיקה/, 'columns'],
  [/בינלאומי/, 'globe'],
  [/מסחר|תאגיד/, 'briefcase'],
  [/מקרקעין|נדל/, 'home'],
  [/זכויות|רווחה|שוויון/, 'heart'],
  [/רגולציה|טכנולוג|סייבר|פרטיות/, 'chip'],
  [/מיס|כלכל/, 'coins'],
];

const DOM_TONE: [RegExp, string][] = [
  [/פליל/, '#7a2e3a'],
  [/אזרח/, '#2e4a7a'],
  [/ציבורי|חוקתי|חקיקה/, '#2f5d4f'],
  [/בינלאומי/, '#2b6070'],
  [/מסחר|תאגיד/, '#5b3a6b'],
  [/מקרקעין|נדל/, '#6b5a2e'],
  [/זכויות|רווחה|שוויון/, '#94582a'],
  [/רגולציה|טכנולוג|סייבר|פרטיות/, '#3d4785'],
  [/מיס|כלכל/, '#4b5563'],
];

export const DEFAULT_DOMAIN_TONE = '#4a5568';
export const DEFAULT_DOMAIN_ICON = 'building';

export function domainTone(domain: string | undefined): string {
  for (const [re, c] of DOM_TONE) if (re.test(domain ?? '')) return c;
  return DEFAULT_DOMAIN_TONE;
}

export function domainIcon(domain: string | undefined): string {
  for (const [re, i] of DOM_ICON) if (re.test(domain ?? '')) return i;
  return DEFAULT_DOMAIN_ICON;
}
